import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import emojiData from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { FlowVariableInsertPicker } from "../../components/flow-variables";

function clampQuestionAttempts(raw, fallback = 3) {
  const n = parseInt(String(raw ?? "").trim(), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(10, Math.max(1, n));
}

const ANSWER_FORMAT_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "True/False" },
  { value: "email", label: "Email" },
  { value: "regex", label: "Regex" },
];

const EMOJIS = [
  "😀",
  "😁",
  "😂",
  "😍",
  "🤩",
  "😎",
  "😊",
  "🙂",
  "🙌",
  "🎉",
  "❤️",
  "👍",
  "🙏",
];

export const QuestionMessageNode = ({ data, id, selected = false }) => {
  const availableVariables = Array.isArray(data?.availableVariables)
    ? data.availableVariables
    : [];
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);

  const [questionText, setQuestionText] = useState(data.body || "");
  const [answerButtons, setAnswerButtons] = useState(data.labels || []);
  const [answerVariable, setAnswerVariable] = useState(
    data.answerVariable || ""
  );
  const [answerFormat, setAnswerFormat] = useState(data.answerFormat || "any");
  const [maxAttempts, setMaxAttempts] = useState(data.maxAttempts ?? 3);
  const [validationErrorMessage, setValidationErrorMessage] = useState(
    data.validationErrorMessage || ""
  );
  const [regexPattern, setRegexPattern] = useState(data.regexPattern || "");
  const [abandonMessage, setAbandonMessage] = useState(data.abandonMessage || "");

  const [editQuestionText, setEditQuestionText] = useState(data.body || "");
  const [editAnswerButtons, setEditAnswerButtons] = useState(
    Array.isArray(data.labels) ? data.labels : []
  );
  const [newAnswerVariant, setNewAnswerVariant] = useState("");
  const [editAnswerVariable, setEditAnswerVariable] = useState(
    data.answerVariable || ""
  );
  const [editAnswerFormat, setEditAnswerFormat] = useState(
    data.answerFormat || "any"
  );
  const [editMaxAttempts, setEditMaxAttempts] = useState(
    String(data.maxAttempts ?? 3)
  );
  const [editValidationErrorMessage, setEditValidationErrorMessage] = useState(
    data.validationErrorMessage || ""
  );
  const [editRegexPattern, setEditRegexPattern] = useState(
    data.regexPattern || ""
  );
  const [editAbandonMessage, setEditAbandonMessage] = useState(
    data.abandonMessage || ""
  );

  const [questionError, setQuestionError] = useState("");
  const [answerVariableError, setAnswerVariableError] = useState("");

  const textareaRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    setQuestionText(data.body || "");
    setAnswerButtons(Array.isArray(data.labels) ? data.labels : []);
    setAnswerVariable(data.answerVariable || "");
    setAnswerFormat(data.answerFormat || "any");
    setMaxAttempts(data.maxAttempts ?? 3);
    setValidationErrorMessage(data.validationErrorMessage || "");
    setRegexPattern(data.regexPattern || "");
    setAbandonMessage(data.abandonMessage || "");
  }, [
    data.body,
    data.labels,
    data.answerVariable,
    data.answerFormat,
    data.maxAttempts,
    data.validationErrorMessage,
    data.regexPattern,
    data.abandonMessage,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const isHandleConnected = useCallback(
    (handleId) => {
      if (!data.edges || !Array.isArray(data.edges)) {
        return false;
      }
      return data.edges.some((edge) => edge.sourceHandle === handleId);
    },
    [data.edges]
  );

  const validateQuestion = (value) => {
    if (!value.trim()) {
      setQuestionError("Question text is required");
      return false;
    }
    if (value.length > 1024) {
      setQuestionError("Question cannot exceed 1024 characters");
      return false;
    }
    setQuestionError("");
    return true;
  };

  const validateAnswerVariable = (value) => {
    if (!value) {
      setAnswerVariableError("");
      return true;
    }
    const isValid = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);
    if (!isValid) {
      setAnswerVariableError(
        "Use letters, numbers, and underscore; cannot start with a number"
      );
      return false;
    }
    setAnswerVariableError("");
    return true;
  };

  const handleQuestionChange = (e) => {
    const value = e.target.value;
    setEditQuestionText(value);
    validateQuestion(value);
  };

  const handleAddAnswerVariant = () => {
    const trimmed = newAnswerVariant.trim();
    if (!trimmed) return;
    if (editAnswerButtons.length >= 10) return;

    const newButton = {
      label: trimmed,
      label_id: `btn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    };
    setEditAnswerButtons([...editAnswerButtons, newButton]);
    setNewAnswerVariant("");
  };

  const handleDeleteAnswerVariant = (index) => {
    const updated = editAnswerButtons.filter((_, i) => i !== index);
    setEditAnswerButtons(updated);
  };

  const insertAtCursor = (textToInsert) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setEditQuestionText((prev) => `${prev}${textToInsert}`);
      return;
    }

    const start = textarea.selectionStart ?? editQuestionText.length;
    const end = textarea.selectionEnd ?? editQuestionText.length;
    const currentValue = editQuestionText;

    const updated =
      currentValue.slice(0, start) + textToInsert + currentValue.slice(end);
    setEditQuestionText(updated);
    setQuestionError("");

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + textToInsert.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleVariableInsert = (variable) => {
    const placeholder = `{{${variable}}}`;
    insertAtCursor(placeholder);
  };

  const handleEmojiInsert = (emoji) => {
    insertAtCursor(emoji);
  };

  const handleBoldClick = () => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setEditQuestionText((prev) => `*${prev}*`);
      return;
    }

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const currentValue = editQuestionText;

    let updated;
    let cursorStart;

    if (start === end) {
      updated =
        currentValue.slice(0, start) + "**" + currentValue.slice(end);
      cursorStart = start + 1;
    } else {
      const selected = currentValue.slice(start, end);
      updated =
        currentValue.slice(0, start) +
        `*${selected}*` +
        currentValue.slice(end);
      cursorStart = start + selected.length + 2;
    }

    setEditQuestionText(updated);
    validateQuestion(updated);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorStart, cursorStart);
    }, 0);
  };

  const handleEmojiSelect = (emoji) => {
    insertAtCursor(emoji.native || "");
    setShowEmojiPicker(false);
  };

  const handleSave = () => {
    const isQuestionValid = validateQuestion(editQuestionText);
    const isAnswerVarValid = validateAnswerVariable(editAnswerVariable);

    if (!isQuestionValid || !isAnswerVarValid) {
      return;
    }

    if (data.onChangeBody) data.onChangeBody(id, editQuestionText);
    if (data.onChangeLabels) data.onChangeLabels(id, editAnswerButtons);
    if (data.onChangeAnswerVariable)
      data.onChangeAnswerVariable(id, editAnswerVariable || "");
    if (data.onChangeQuestionValidation) {
      const format = editAnswerFormat || "any";
      data.onChangeQuestionValidation(id, {
        answerFormat: format,
        ...(format !== "any"
          ? {
              maxAttempts: clampQuestionAttempts(editMaxAttempts, 3),
              validationErrorMessage: editValidationErrorMessage || "",
              regexPattern: editRegexPattern || "",
            }
          : {}),
      });
    }

    setQuestionText(editQuestionText);
    setAnswerButtons(editAnswerButtons);
    setAnswerVariable(editAnswerVariable || "");
    setAnswerFormat(editAnswerFormat || "any");
    setMaxAttempts(clampQuestionAttempts(editMaxAttempts, 3));
    setValidationErrorMessage(editValidationErrorMessage || "");
    setRegexPattern(editRegexPattern || "");
    setAbandonMessage(editAbandonMessage || "");

    setShowEditPopup(false);
    setShowMenu(false);
  };

  const handleCancel = () => {
    setEditQuestionText(questionText || "");
    setEditAnswerButtons(Array.isArray(answerButtons) ? answerButtons : []);
    setEditAnswerVariable(answerVariable || "");
    setEditAnswerFormat(answerFormat || "any");
    setEditMaxAttempts(String(maxAttempts ?? 3));
    setEditValidationErrorMessage(validationErrorMessage || "");
    setEditRegexPattern(regexPattern || "");
    setEditAbandonMessage(abandonMessage || "");
    setQuestionError("");
    setAnswerVariableError("");
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const handleEdit = () => {
    setEditQuestionText(questionText || "");
    setEditAnswerButtons(Array.isArray(answerButtons) ? answerButtons : []);
    setEditAnswerVariable(answerVariable || "");
    setEditAnswerFormat(answerFormat || "any");
    setEditMaxAttempts(String(maxAttempts ?? 3));
    setEditValidationErrorMessage(validationErrorMessage || "");
    setEditRegexPattern(regexPattern || "");
    setEditAbandonMessage(abandonMessage || "");
    setQuestionError("");
    setAnswerVariableError("");
    setShowEditPopup(true);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
    setShowMenu(false);
  };

  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const popupStyle = {
    background: "white",
    borderRadius: "8px",
    padding: "0",
    width: "600px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const errorStyle = {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
  };

  const charCountStyle = {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "right",
    marginTop: "4px",
    marginBottom: "4px",
  };

  const BODY_PREVIEW_MAX = 80;
  const truncateForPreview = (value) =>
    value && value.length > BODY_PREVIEW_MAX
      ? `${value.slice(0, BODY_PREVIEW_MAX)}…`
      : value;

  const renderMarkdownBold = (value) => {
    if (!value) return null;
    const parts = [];
    const regex = /\*([^*]+)\*/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.slice(lastIndex, match.index));
      }
      parts.push(
        <strong key={parts.length}>
          {match[1]}
        </strong>
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < value.length) {
      parts.push(value.slice(lastIndex));
    }

    return parts;
  };

  const menuStyle = {
    position: "absolute",
    top: 30,
    right: 8,
    background: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 2,
    minWidth: "100px",
  };

  const menuItemStyle = {
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    borderBottom: "1px solid #eee",
    transition: "background-color 0.2s",
  };

  const hasButtons =
    Array.isArray(answerButtons) &&
    answerButtons.length > 0 &&
    answerButtons.some((btn) => {
      if (typeof btn === "string") return btn.trim();
      return (btn.label || "").trim();
    });

  const mainHandleId = `${id}-main`;
  const mainHandleConnected = isHandleConnected(mainHandleId);

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: "6px",
          padding: "8px",
          width: "180px",
          minHeight: "70px",
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            zIndex: 1,
            userSelect: "none",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            height: "11px",
          }}
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋮
        </div>

        {showMenu && (
          <div ref={menuRef} style={menuStyle}>
            <div
              style={menuItemStyle}
              onClick={handleEdit}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              ✏️ Edit
            </div>
            <div
              style={{ ...menuItemStyle, borderBottom: "none" }}
              onClick={handleDelete}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          style={{ background: selected ? "#1976d2" : "#6c757d" }}
        />

        {!hasButtons && (
          <Handle
            type="source"
            position={Position.Right}
            id={mainHandleId}
            style={{
              background: mainHandleConnected
                ? "#28a745"
                : selected
                ? "#1976d2"
                : "#6c757d",
              width: "8px",
              height: "8px",
              right: "-4px",
              top: "50%",
              transform: "translateY(-50%)",
              border: "2px solid white",
              opacity: mainHandleConnected ? 0.7 : 1,
              cursor: mainHandleConnected ? "not-allowed" : "crosshair",
            }}
          />
        )}

        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontWeight: "bold",
              color: selected ? "#1976d2" : "#6c757d",
              marginBottom: "6px",
              fontSize: "11px",
            }}
          >
            Question
          </div>
          {questionText && questionText.trim() ? (
            <div
              style={{ fontSize: "13px", color: "#444", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  minHeight: "24px",
                  textAlign: "left",
                  whiteSpace: "pre-wrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {renderMarkdownBold(truncateForPreview(questionText))}
              </div>
            </div>
          ) : (
            <div
              style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  fontStyle: "italic",
                  textAlign: "left",
                }}
              >
                Click Edit to set question
              </div>
            </div>
          )}

          {hasButtons && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "100%",
              }}
            >
              {answerButtons.map((btn, index) => {
                const text =
                  typeof btn === "object" && btn.label ? btn.label : btn;
                const handleId = `${id}-label-${index}`;
                const connected = isHandleConnected(handleId);
                return (
                  <div
                    key={`${id}-label-${index}-${
                      connected ? "connected" : "disconnected"
                    }`}
                    style={{
                      position: "relative",
                      background: connected ? "#28a745" : "#0d6efd",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      width: "100%",
                      textAlign: "center",
                      boxSizing: "border-box",
                      border: `1px solid ${
                        connected ? "#1e7e34" : "#0b5ed7"
                      }`,
                      minHeight: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: connected ? 0.7 : 1,
                    }}
                  >
                    {text}
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={handleId}
                      style={{
                        background: connected
                          ? "#28a745"
                          : selected
                          ? "#1976d2"
                          : "#6c757d",
                        width: "6px",
                        height: "6px",
                        right: "-3px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "1px solid white",
                        opacity: connected ? 0.5 : 1,
                        cursor: connected ? "not-allowed" : "crosshair",
                        pointerEvents: connected ? "none" : "auto",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {answerVariable && (
            <div
              style={{
                marginTop: "6px",
                fontSize: "10px",
                color: "#6c757d",
              }}
            >
              Saves to variable:{" "}
              <span style={{ fontWeight: "bold" }}>{answerVariable}</span>
            </div>
          )}
        </div>
      </div>

      {showEditPopup &&
        createPortal(
          <div style={popupOverlayStyle} onClick={handleCancel}>
            <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "24px 24px 16px 24px",
                  borderBottom: "1px solid #e9ecef",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 1,
                  borderRadius: "8px 8px 0 0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "#333",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  Set a question
                </h3>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={handleSave}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #28a745",
                      borderRadius: "4px",
                      backgroundColor: "#28a745",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #6c757d",
                      borderRadius: "4px",
                      backgroundColor: "white",
                      color: "#6c757d",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ padding: "0 24px 24px 24px" }}>
                <div style={{ marginTop: "8px", marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Question text
                  </label>
                  <textarea
                    ref={textareaRef}
                    value={editQuestionText}
                    onChange={handleQuestionChange}
                    placeholder="Please enter your question..."
                    rows={4}
                    style={{
                      ...inputStyle,
                      borderColor: questionError ? "#dc3545" : "#ddd",
                      resize: "vertical",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "6px",
                      gap: "8px",
                      fontSize: "12px",
                      color: "#555",
                    }}
                  >
                    <div style={{ flex: 1 }} />
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={handleBoldClick}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        <FormatBoldIcon style={{ fontSize: 14 }} />
                        <span>Bold</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                          backgroundColor: "#ffffff",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        <InsertEmoticonIcon style={{ fontSize: 16 }} />
                        <span>Emoji</span>
                      </button>
                    </div>
                  </div>
                  <FlowVariableInsertPicker
                    customVariables={availableVariables}
                    onInsert={handleVariableInsert}
                    label="Insert variable into question"
                  />
                  {showEmojiPicker && (
                    <div
                      style={{
                        position: "fixed",
                        zIndex: 10000,
                        right: 40,
                        bottom: 160,
                      }}
                    >
                      <Picker
                        data={emojiData}
                        onEmojiSelect={handleEmojiSelect}
                        theme="light"
                      />
                    </div>
                  )}
                  <div style={charCountStyle}>
                    {editQuestionText.length}/1024
                  </div>
                  {questionError && (
                    <div style={errorStyle}>{questionError}</div>
                  )}
                </div>

                <div
                  style={{
                    marginBottom: "20px",
                    paddingTop: "8px",
                    borderTop: "1px solid #e9ecef",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Add answer variant (optional)
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "10px",
                    }}
                  >
                    <input
                      type="text"
                      value={newAnswerVariant}
                      onChange={(e) => setNewAnswerVariant(e.target.value)}
                      placeholder="Answer button text"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={handleAddAnswerVariant}
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #0d6efd",
                        borderRadius: "4px",
                        backgroundColor: "#0d6efd",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Create
                    </button>
                  </div>

                  {editAnswerButtons.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {editAnswerButtons.map((btn, index) => {
                        const text =
                          typeof btn === "object" && btn.label
                            ? btn.label
                            : btn;
                        return (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "6px 8px",
                              borderRadius: "4px",
                              border: "1px solid #dee2e6",
                              backgroundColor: "#f8f9fa",
                              fontSize: "13px",
                            }}
                          >
                            <span>{text}</span>
                            <button
                              type="button"
                              onClick={() => handleDeleteAnswerVariant(index)}
                              style={{
                                padding: "4px 6px",
                                borderRadius: "4px",
                                border: "1px solid #dc3545",
                                backgroundColor: "white",
                                color: "#dc3545",
                                cursor: "pointer",
                                fontSize: "11px",
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    marginBottom: "8px",
                    paddingTop: "8px",
                    borderTop: "1px solid #e9ecef",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Attribute format
                  </label>
                  <select
                    value={editAnswerFormat}
                    onChange={(e) => setEditAnswerFormat(e.target.value)}
                    style={{ ...inputStyle, marginBottom: "10px" }}
                  >
                    {ANSWER_FORMAT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {editAnswerFormat !== "any" && (
                    <>
                      <div style={{ marginBottom: "10px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "4px",
                            fontSize: "13px",
                            color: "#555",
                          }}
                        >
                          Number of attempts
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={editMaxAttempts}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
                            setEditMaxAttempts(digits);
                          }}
                          onBlur={() => {
                            setEditMaxAttempts(
                              String(clampQuestionAttempts(editMaxAttempts, 3))
                            );
                          }}
                          placeholder="3"
                          style={{ ...inputStyle, width: "80px" }}
                        />
                      </div>
                      {editAnswerFormat === "regex" && (
                        <div style={{ marginBottom: "10px" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              fontSize: "13px",
                              color: "#555",
                            }}
                          >
                            Regex pattern
                          </label>
                          <input
                            type="text"
                            value={editRegexPattern}
                            onChange={(e) => setEditRegexPattern(e.target.value)}
                            placeholder="e.g. ^[A-Z]{2}\\d{6}$"
                            style={inputStyle}
                          />
                        </div>
                      )}
                      <div style={{ marginBottom: "10px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "4px",
                            fontSize: "13px",
                            color: "#555",
                          }}
                        >
                          Validation error message
                        </label>
                        <textarea
                          value={editValidationErrorMessage}
                          onChange={(e) =>
                            setEditValidationErrorMessage(e.target.value)
                          }
                          placeholder="Type message"
                          rows={2}
                          style={{ ...inputStyle, resize: "vertical" }}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div
                  style={{
                    marginBottom: "8px",
                    paddingTop: "8px",
                    borderTop: "1px solid #e9ecef",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Save answer in a variable
                  </label>
                  <input
                    type="text"
                    value={editAnswerVariable}
                    onChange={(e) => {
                      setEditAnswerVariable(e.target.value);
                      validateAnswerVariable(e.target.value);
                    }}
                    placeholder="e.g. shipping_address"
                    style={inputStyle}
                  />
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginTop: "4px",
                    }}
                  >
                    This name will be stored as `answer_variable` for this
                    question.
                  </div>
                  {answerVariableError && (
                    <div style={errorStyle}>{answerVariableError}</div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

